import { Row, Table } from "@tanstack/react-table";
import { MutableRefObject, useCallback } from "react";

import { SelectionIntent } from "../context";

const getRowRange = (
  rows: Row<any>[],
  currentID: number,
  selectedID: number,
): Row<any>[] => {
  const rangeStart = selectedID > currentID ? currentID : selectedID;
  const rangeEnd = rangeStart === currentID ? selectedID : currentID;
  return rows.slice(rangeStart, rangeEnd + 1);
};

interface RowActivationOptions {
  table: Table<any>;
  selectable: boolean;
  editMode: boolean;
  selectionIntent?: MutableRefObject<SelectionIntent | null>;
}

export function useRowActivation({
  table,
  selectable,
  editMode,
  selectionIntent,
}: RowActivationOptions) {
  const onRowClick = table.options.meta?.onRowClick as
    ((row: Row<any>) => void) | undefined;

  const markRowIntent = useCallback(() => {
    if (selectionIntent) selectionIntent.current = "row";
  }, [selectionIntent]);

  const selectRange = useCallback(
    (row: Row<any>, shouldSelect: boolean) => {
      markRowIntent();
      const selectedRows = table.getSelectedRowModel().rows;

      if (selectedRows.length > 0) {
        const lastSelectedRow = selectedRows[selectedRows.length - 1];
        const { rows: allRows } = table.getRowModel();
        const currentRowIndex = Number(row.id);
        const lastRowIndex = Number(lastSelectedRow.id);

        const rowsToToggle = getRowRange(
          allRows,
          currentRowIndex,
          lastRowIndex,
        );

        rowsToToggle.forEach((_row) => _row.toggleSelected(shouldSelect));
      } else {
        row.toggleSelected(shouldSelect);
      }
    },
    [markRowIntent, table],
  );

  const toggleSelection = useCallback(
    (row: Row<any>, shiftKey: boolean) => {
      const shouldSelect = !row.getIsSelected();
      if (shiftKey) {
        selectRange(row, shouldSelect);
      } else {
        markRowIntent();
        row.toggleSelected(shouldSelect);
      }
    },
    [markRowIntent, selectRange],
  );

  const activateRow = useCallback(
    (row: Row<any>, shiftKey: boolean) => {
      if (!selectable || editMode) return false;
      if (shiftKey) {
        selectRange(row, !row.getIsSelected());
      } else if (onRowClick) {
        onRowClick(row);
      } else {
        markRowIntent();
        row.toggleSelected();
      }
      return true;
    },
    [editMode, markRowIntent, onRowClick, selectRange, selectable],
  );

  const toggleRowFromKeyboard = useCallback(
    (row: Row<any>) => {
      if (!selectable || editMode) return false;
      markRowIntent();
      row.toggleSelected();
      return true;
    },
    [editMode, markRowIntent, selectable],
  );

  return { activateRow, toggleSelection, toggleRowFromKeyboard };
}
